<template>
  <section ref="amenitiesSection">
    <THeading
      :level="3"
      tag="h3"
      class="mb-4"
    >
      {{ isPark ? t('parkAmenities') : t('title') }}
    </THeading>

    <div class="grid sm:grid-cols-2 gap-6 md:gap-x-8 mt-2">
      <div
        v-for="item in computedAmenities"
        :key="item.label"
        :class="['items-center gap-3', {
          'inline-flex': item.visible,
          'hidden': !item.visible,
        }]"
      >
        <TIcon
          :icon="item.icon"
          size="xl"
          fixed-width
        />
        {{ t(`amenities.${item.label}`) }}
      </div>
    </div>

    <div
      v-if="computedCanShowMore"
      class="mt-4"
    >
      <TButton
        variant="link"
        @click="showMore"
      >
        {{ expanded ? t('hide') : t('show') }}
      </TButton>
    </div>
  </section>
</template>

<script setup lang="ts">
import { type AMENITIES, type PARK_AMENITIES, AMENITIES_ICON_MAP } from '~/constants/amenities'

const props = defineProps<{
  isPark?: boolean
}>()

const { t } = useI18n()

const {
  rvIsDeliveryOnly,
  rvInteriorFeatures,
  rvParkAmenities,
} = useRvDetails()

const expanded = ref(false)
const amountToShow = ref(4)
const amenitiesSection = ref<HTMLElement>()

const filteredInteriorFeatures = computed(() => {
  const itemsToRemove = ['AudioInputs', 'CeilingFan', 'Washer_Dryer', 'SmokingAllowed', 'EmergencyReadyUnit']
  if (rvIsDeliveryOnly.value) {
    itemsToRemove.push('Parking')
  }
  return rvInteriorFeatures.value.filter((amenity) => !itemsToRemove.includes(amenity))
})

const computedAmenities = computed(() => {
  const items = props.isPark ? rvParkAmenities.value : filteredInteriorFeatures.value

  return items?.map((amenity, index) => ({
    label: amenity,
    icon: AMENITIES_ICON_MAP[amenity as AMENITIES | PARK_AMENITIES],
    visible: expanded.value || index < amountToShow.value,
  })) ?? []
})

const computedCanShowMore = computed(() => {
  return computedAmenities.value.length > amountToShow.value
})

function showMore() {
  expanded.value = !expanded.value

  if (!expanded.value) {
    amenitiesSection.value?.scrollIntoView()
  }
}
</script>

<i18n src="~~/i18n/locales/common/amenities.json" lang="json" />

<i18n lang="json">
{
  "en": {
    "title": "What this RV offers",
    "parkAmenities": "Park amenities",
    "show": "Show all amenities",
    "hide": "Hide amenities"
  },
  "fr": {
    "title": "Ce que ce VR offre",
    "parkAmenities": "Commodités du parc",
    "show": "Montrer toutes les commodités",
    "hide": "Cacher les commodités"
  }
}
</i18n>
